<template>
  <div class="col-md-8 offset-md-2">
    <ValidationObserver ref="observer" v-slot="{ invalid }">
      <form @submit.prevent="submit">
        <!-- Station Logo -->
        <div class="form-group text-center mb-4">
          <label class="d-block mb-2">
            <strong>Station Logo</strong>
          </label>
          <div class="logo-preview-container" @click="chooseLogo">
            <img
              :src="logoPreview || getLogoUrl()"
              class="logo-preview"
              alt="Station Logo"
            />
            <div class="logo-overlay">
              <i class="fas fa-camera"></i>
              <span>Change Logo</span>
            </div>
          </div>
          <input
            type="file"
            ref="logoInput"
            class="d-none"
            accept="image/*"
            @change="onLogoSelected"
          />
          <small class="text-muted d-block mt-2">Click to upload a new logo (max 2MB)</small>
        </div>

        <!-- Station Description -->
        <div class="form-group">
          <label for="description">
            <strong>Station Description</strong>
          </label>
          <validation-provider name="description" rules="max:500" v-slot="{ errors }">
            <textarea
              id="description"
              class="form-control"
              placeholder="Enter a description for your station..."
              v-model="form.description"
              rows="4"
              maxlength="500"
            ></textarea>
            <small class="text-muted">{{ (form.description || '').length }}/500 characters</small>
            <span class="text-danger d-block">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <!-- Station Info (Read Only) -->
        <div class="form-group">
          <label>
            <strong>Station Name</strong>
          </label>
          <input
            class="form-control"
            :value="station.name"
            readonly
            disabled
          />
          <small class="text-muted">Contact support to change your station name</small>
        </div>

        <button
          :disabled="invalid || loading"
          type="submit"
          class="mb-5 btn btn-warning btn-block btn-flat"
        >
          {{ loading ? 'Saving...' : 'Update Station Settings' }}
        </button>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { mapState } from "vuex";
import ApiService from "@/plugins/ApiService";
import toast from "@/plugins/toast";
import { FETCH_STATION_INFO } from "@/store/action-types";

export default {
  name: "StationSettings",
  components: {
    ValidationObserver,
    ValidationProvider
  },

  computed: {
    ...mapState({
      station: (state) => state.stations.selectedStation,
      stationId: (state) => state.stations.stationId,
    }),
  },

  data() {
    return {
      loading: false,
      logoPreview: null,
      logoFile: null,
      form: {
        description: ""
      }
    };
  },

  methods: {
    getLogoUrl() {
      if (this.station && this.station.logo_url) {
        return this.station.logo_url;
      }
      return require("@/assets/logo.svg");
    },

    chooseLogo() {
      this.$refs.logoInput.click();
    },

    onLogoSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo file must be less than 2MB");
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }

      this.logoFile = file;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.logoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    async submit() {
      this.loading = true;

      try {
        const formData = new FormData();
        formData.append("station_id", this.stationId);
        formData.append("description", this.form.description || "");

        if (this.logoFile) {
          formData.append("logo", this.logoFile);
        }

        await ApiService.post("settings/update-station", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        toast.success("Station settings updated successfully!");

        // Refresh station info to get updated data
        await this.$store.dispatch(FETCH_STATION_INFO, {
          id: this.stationId,
          repeat: false
        });

        // Clear the logo file after successful upload
        this.logoFile = null;
      } catch (error) {
        console.error("Failed to update station settings:", error);
        toast.error(error.response?.data?.message || "Failed to update station settings");
      } finally {
        this.loading = false;
      }
    }
  },

  mounted() {
    // Initialize form with current station data
    if (this.station) {
      this.form.description = this.station.description || "";
    }
  },

  watch: {
    station: {
      handler(newStation) {
        if (newStation) {
          this.form.description = newStation.description || "";
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.logo-preview-container {
  position: relative;
  width: 150px;
  height: 150px;
  margin: 0 auto;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  border: 2px dashed #ddd;
}

.logo-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.logo-preview-container:hover .logo-overlay {
  opacity: 1;
}

.logo-overlay i {
  font-size: 24px;
  margin-bottom: 5px;
}

.logo-overlay span {
  font-size: 12px;
}
</style>
